import { defineStore } from 'pinia';
import { DeferAPaymentService as _deferAPayment } from '@/services/store/defer-a-payment.service';
import { QuoteSessionService as _quoteSessionService } from '@/services/store/quote-session.service';
import { ref } from 'vue';
import {
  Coverage,
  IJeopardy,
  InsurancePolicy,
  IReviewSummary,
  IServiceResponseError,
  ISitecoreReviewSummaryItem,
  PetCoverage,
  ResidentialCoverage,
  VehicleCoverage,
} from '@/interfaces';
import {
  ApplyRulesResponseOfDeferPaymentViewModel,
  IDeferPaymentUpdateModel,
  IMaxDeferPaymentDaysResponse,
  IMaxDeferPaymentRequest,
  IPaymentInstrument,
} from '@/interfaces/self-service/defer-payment.interface';
import { JP_GENERIC_CODE } from '@/constants/jeopardy-codes.const';
import { replaceBookmarks } from '@/utils/bookmark.replace';
import { format } from 'date-fns';
import { getInsuranceTypeDisplayName } from '@/utils/editable-steps/quote-content-builder';
import { cloneDeep } from 'lodash-es';
import { toCurrency } from '@/utils/convert';
import { IFormAnalytics } from '@/interfaces/forms/analytics-interface';
import { FormEvents } from '@/enums/form-events.enum';
import { useAnalyticsStore } from '../analytics/analytics.state';
import { formatMaskedCardNumber } from '@/utils/text-transforms';
import { LONG_MONTH_DATE } from '@/constants/formats';

export const useDeferAPaymentStore = defineStore('DeferAPayment', () => {
  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const quoteSessionId = ref<string>();
  const currentPolicy = ref<InsurancePolicy>();
  const model = ref<IDeferPaymentUpdateModel>();
  const paymentInstrument = ref<IPaymentInstrument>();
  const jeopardies = ref<IJeopardy[]>([]);
  const errors = ref<IServiceResponseError[]>([]);
  const businessRuleResponse = ref<ApplyRulesResponseOfDeferPaymentViewModel>();
  const maxDeferDaysResponse = ref<IMaxDeferPaymentDaysResponse>();
  const analyticsStore = useAnalyticsStore();

  const getMaxDeferPaymentDate = async (request: IMaxDeferPaymentRequest) => {
    maxDeferDaysResponse.value = await _deferAPayment.getMaxDeferDays(request);
  };

  const applyBusinessRules = async (requestQuote?: boolean, bindChange?: boolean) => {
    const response = await _deferAPayment.applyBusinessRules({
      bindChange: bindChange,
      requestQuote: requestQuote,
      model: model.value,
      quoteSessionId: quoteSessionId.value,
    });
    errors.value = response.errors;
    if (response.success && response.data) {
      jeopardies.value = response.data.jeopardies;
      businessRuleResponse.value = response.data;
    }
    if (errors.value && errors.value.length > 0) {
      throw errors.value[0].message ?? JP_GENERIC_CODE;
    }
    if (jeopardies.value && jeopardies.value.length > 0) {
      throw jeopardies.value[0].code;
    }
  };

  /**
   * Get Policy
   */
  async function getPolicy() {
    const params = new URLSearchParams(location.search);
    quoteSessionId.value = params.get('quoteSessionId') ?? '';
    if (!quoteSessionId.value) throw JP_GENERIC_CODE;
    const response = await _deferAPayment.getPolicy(quoteSessionId.value);
    errors.value = response.errors;
    if (response.success && response.data) {
      currentPolicy.value = response.data;
    }
    if (errors.value && errors.value.length > 0) {
      throw errors.value[0].message ?? JP_GENERIC_CODE;
    }
  }

  async function fetchPaymentDetails() {
    const paymentDetails = await _deferAPayment.getPaymentInstrument(quoteSessionId.value ?? '');
    paymentInstrument.value = paymentDetails;
  }

  function getCoverageDescription(coverage?: Coverage): string {
    const description = coverage?.isPet
      ? (coverage as PetCoverage).petName
      : coverage?.isHome
        ? (coverage as ResidentialCoverage).riskAddress.formattedStreet
        : `${(coverage as VehicleCoverage)?.vehicle?.year} ${(coverage as VehicleCoverage)?.vehicle?.make} ${(coverage as VehicleCoverage)?.vehicle?.model}`;
    return description;
  }

  /**
   * Generate a review summary model for review and confirmation steps
   */
  function getReviewSummaryModel(summaryFields: any, dictionary: any, selectedDate?: Date): IReviewSummary[] {
    const summaryFieldsResult: IReviewSummary[] = [];

    const isBank = paymentInstrument.value?.bankAccountDetails;
    const bsb = paymentInstrument.value?.bankAccountDetails?.bankStateBranchNumber;
    const accountNumber = paymentInstrument.value?.bankAccountDetails?.accountNumber;
    const accountName = paymentInstrument.value?.bankAccountDetails?.nameOfAccount;
    const cardNumber = formatMaskedCardNumber(paymentInstrument.value?.creditCardDetails?.maskedCardNumber ?? '');
    const cardExpiry = paymentInstrument.value?.creditCardDetails?.cardExpiry;
    const nameOnCard = paymentInstrument.value?.creditCardDetails?.cardHolder;
    const cardType = paymentInstrument.value?.creditCardDetails?.cardType;
    const paymentTerm = currentPolicy.value?.currentTermPaymentDetails?.nextInvoiceDueDate
      ? currentPolicy.value?.currentTermPaymentDetails
      : currentPolicy.value?.renewalTermPaymentDetails;
    const dueAmount = toCurrency(paymentTerm?.nextInvoiceAmountDue);
    const dueDate = selectedDate
      ? format(selectedDate, LONG_MONTH_DATE)
      : paymentTerm?.nextInvoiceDueDate
        ? format(paymentTerm?.nextInvoiceDueDate, LONG_MONTH_DATE)
        : '';
    const changeEffectiveDateObj = {
      bsb,
      accountNumber,
      accountName,
      cardNumber,
      cardExpiry,
      cardType,
      dueAmount,
      dueDate,
      nameOnCard,
    };

    summaryFieldsResult.push(replaceBookmarks(summaryFields[0], changeEffectiveDateObj));

    let summaryItem = {
      formContentId: summaryFields[1].formContentId,
      summaryTitle: summaryFields[1].summaryTitle,
      icon: summaryFields[1].icon,
      items: summaryFields[1].items,
    };
    summaryItem = replaceBookmarks(summaryItem, changeEffectiveDateObj);
    summaryFieldsResult.push(summaryItem);
    const section = summaryFieldsResult[1];
    section.items = section.items.filter((item) => {
      if (isBank) {
        return (
          item.name !== 'DP-CardType' &&
          item.name !== 'DP-NameOnCard' &&
          item.name !== 'DP-CardNumber' &&
          item.name !== 'DP-CardExpiry'
        );
      } else {
        return item.name !== 'DP-AccountNumber' && item.name !== 'DP-AccountName' && item.name !== 'DP-BSB';
      }
    });
    const summaryItem1 = {
      formContentId: summaryFields[2].formContentId,
      summaryTitle: summaryFields[2].summaryTitle,
      icon: summaryFields[2].icon,
      items: summaryFields[2].items,
    };
    const cloneItem = cloneDeep(summaryItem1.items[0]);
    summaryItem1.items = [];
    if (currentPolicy.value?.coverages?.length) {
      for (let index = 0; index < currentPolicy.value?.coverages?.length; index++) {
        const cloneItemTemp = cloneDeep(cloneItem);
        const coverage = currentPolicy.value?.coverages[index];
        cloneItemTemp.fields.title.value = `${currentPolicy.value?.policyNumber} - ${getInsuranceTypeDisplayName(dictionary, coverage?.coverageCode ?? '')}`;
        cloneItemTemp.fields.content.value = getCoverageDescription(coverage);
        summaryItem1.items.push(cloneItemTemp);
      }
    }
    summaryFieldsResult.push(summaryItem1);
    return summaryFieldsResult.map((field: any) => ({
      ...field,
      items: field.items.map((item: ISitecoreReviewSummaryItem) => {
        return {
          ...item,
          ...item.fields,
          editGoToStep: item.fields.editStep?.value,
          showEdit: item.fields.editStep?.value ?? false,
        };
      }),
    }));
  }

  function trackFormEvent(dictionary: any, stepName: string, formEvent: FormEvents) {
    try {
      const paymentTerm = currentPolicy.value?.currentTermPaymentDetails?.nextInvoiceDueDate
        ? currentPolicy.value?.currentTermPaymentDetails
        : currentPolicy.value?.renewalTermPaymentDetails;
      const event: IFormAnalytics = {
        event: formEvent,
        form: {
          stepName: stepName,
          productName: getInsuranceTypeDisplayName(dictionary, currentPolicy.value?.coverages[0]?.coverageCode ?? ''),
          amount: paymentTerm?.nextInvoiceAmountDue,
        },
      };
      if (currentPolicy.value?.coverages[0]?.isMotor) {
        event.form.detail = {
          makeName: (currentPolicy.value?.coverages[0] as VehicleCoverage).vehicle?.make,
          modelName: (currentPolicy.value?.coverages[0] as VehicleCoverage).vehicle?.model,
          vehicleYear: (currentPolicy.value?.coverages[0] as VehicleCoverage).vehicle?.year,
        };
      }
      analyticsStore.pushAnalytics(event);
    } catch (error) {
      console.error('Error pushing data to analytics', error);
    }
  }
  const refreshQuoteSession = async () => {
    try {
      await _quoteSessionService.refreshQuoteSession();
    } catch (error) {
      console.error('Error refreshing portfolio', error);
    }
  };
  const deleteQuoteSession = async () => {
    try {
      await _quoteSessionService.deleteQuoteSession(quoteSessionId.value ?? '');
    } catch (e) {
      //ignore if delete quote session doesn't work
      console.error('Error delete quote session', e);
    }
  };
  function $reset() {}

  return {
    getMaxDeferPaymentDate,
    $reset,
    getPolicy,
    currentPolicy,
    model,
    businessRuleResponse,
    maxDeferDaysResponse,
    applyBusinessRules,
    fetchPaymentDetails,
    getReviewSummaryModel,
    trackFormEvent,
    refreshQuoteSession,
    deleteQuoteSession,
  };
});
